import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Send, 
  MessageSquare, 
  Loader2, 
  Building2,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagesPage = () => {
  const { t } = useLanguage();
  const { user, isProfessional, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  
  const [conversations, setConversations] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Check if user is professional (wait for auth to load)
  useEffect(() => {
    if (!authLoading && !isProfessional) {
      navigate('/dashboard');
    }
  }, [isProfessional, authLoading, navigate]);

  // Fetch conversations (only when auth is ready)
  useEffect(() => {
    if (!authLoading && isProfessional) {
      fetchConversations();
    }
  }, [authLoading, isProfessional]);

  // Handle pre-selected recipient
  useEffect(() => {
    const toUserId = searchParams.get('to');
    if (!toUserId) return;
    
    // Check if there's already a conversation with this user
    const existing = conversations.find(c => c.user_id === toUserId);
    if (existing) {
      handleSelectConversation(existing);
    } else if (!loading) {
      // New conversation - fetch partner info and create placeholder
      fetchPartnerInfo(toUserId);
    }
  }, [searchParams, conversations, loading]);

  const fetchPartnerInfo = async (userId) => {
    try {
      const response = await axios.get(`${API}/professionals`);
      const professionals = response.data || [];
      const partner = professionals.find(p => p.user_id === userId);
      
      setSelectedPartner({
        user_id: userId,
        user_name: partner?.name || 'Professionista',
        company_name: partner?.company_name || ''
      });
      setMessages([]);
    } catch (error) {
      setSelectedPartner({
        user_id: userId,
        user_name: 'Professionista',
        company_name: ''
      });
      setMessages([]);
    }
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = async (conversation) => {
    setSelectedPartner(conversation);
    try {
      const response = await axios.get(`${API}/messages/${conversation.user_id}`);
      setMessages(response.data);
      fetchConversations(); // Refresh to update unread counts
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPartner) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/messages`, {
        recipient_id: selectedPartner.user_id,
        content: newMessage.trim()
      });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common_error'));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="messages-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 
            className="text-3xl font-semibold text-slate-900"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {t('msg_title')}
          </h1>
          <Button 
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="md:hidden"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('nav_dashboard')}
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[600px]">
          {/* Conversations List */}
          <Card className="border border-slate-200 rounded-md overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-medium text-slate-900">Conversations</h2>
            </div>
            <ScrollArea className="h-[540px]">
              {conversations.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">{t('msg_no_conversations')}</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {conversations.map((conv) => (
                    <button
                      key={conv.user_id}
                      onClick={() => handleSelectConversation(conv)}
                      className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                        selectedPartner?.user_id === conv.user_id ? 'bg-slate-100' : ''
                      }`}
                      data-testid={`conversation-${conv.user_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {conv.user_name}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {conv.company_name}
                          </p>
                          <p className="text-sm text-slate-600 truncate mt-1">
                            {conv.last_message}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-slate-400">
                            {formatTime(conv.last_message_time)}
                          </span>
                          {conv.unread_count > 0 && (
                            <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </Card>
          
          {/* Chat Area */}
          <Card className="md:col-span-2 border border-slate-200 rounded-md overflow-hidden flex flex-col">
            {selectedPartner ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-sm flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{selectedPartner.user_name}</p>
                      <p className="text-xs text-slate-500">{selectedPartner.company_name}</p>
                    </div>
                  </div>
                </div>
                
                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((msg, idx) => {
                      const isMe = msg.sender_id === user?.id;
                      const showDate = idx === 0 || 
                        formatDate(messages[idx-1]?.created_at) !== formatDate(msg.created_at);
                      
                      return (
                        <React.Fragment key={msg.id}>
                          {showDate && (
                            <div className="text-center">
                              <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">
                                {formatDate(msg.created_at)}
                              </span>
                            </div>
                          )}
                          <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                            <div 
                              className={`max-w-[70%] p-3 rounded-md ${
                                isMe 
                                  ? 'bg-slate-900 text-white' 
                                  : 'bg-slate-100 text-slate-900'
                              }`}
                              data-testid={`message-${msg.id}`}
                            >
                              <p className="text-sm">{msg.content}</p>
                              <p className={`text-xs mt-1 ${isMe ? 'text-slate-400' : 'text-slate-500'}`}>
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                
                {/* Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={t('msg_placeholder')}
                      className="flex-1 rounded-sm"
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
                      data-testid="send-message"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>{t('msg_start')}</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
